import type { ReactNode } from 'react';
import { Sparkline, type DeltaTone } from '@paige/ui';
import '../admin.css';
import './insights.css';

/** The router's link for a tile that leads to another screen; an in-page #anchor stays a plain link. */
export type RenderLink = (href: string, children: ReactNode, className: string) => ReactNode;

export interface Kpi {
  label: string;
  value: string;
  foot: string;
  /** A change ("+12%") shown before the foot in its own colour. */
  change?: string | undefined;
  tone?: DeltaTone | undefined;
  danger?: boolean | undefined;
  /** The series behind the number, oldest first, drawn small beside the foot. */
  spark?: readonly number[] | undefined;
  /** Where the tile leads: its chart on the page (#id) or another screen. */
  href?: string | undefined;
  /** Instead of a link: what pressing the tile does (filters the list below). */
  onPress?: (() => void) | undefined;
  /** For a pressable tile: whether its filter is on. */
  pressed?: boolean | undefined;
}

function Body({ kpi }: { kpi: Kpi }): ReactNode {
  return (
    <>
      <span className="ad-kpi__label">{kpi.label}</span>
      <span className={['ad-kpi__value', kpi.danger && 'ad-danger'].filter(Boolean).join(' ')}>
        {kpi.value}
      </span>
      <span className="ad-kpi__foot">
        {kpi.change ? (
          <span className={`ad-today__delta ad-today__delta--${kpi.tone ?? 'muted'}`}>
            {kpi.change}
          </span>
        ) : null}
        {kpi.foot}
        {kpi.spark && kpi.spark.length > 1 ? (
          <Sparkline
            values={kpi.spark}
            width={84}
            height={22}
            tone={kpi.tone === 'danger' ? 'danger' : 'accent'}
            label={`${kpi.label}, oldest first`}
          />
        ) : null}
      </span>
    </>
  );
}

/** One number with its label, its change and its little series; it always leads somewhere. */
export function KpiTile({ kpi, renderLink }: { kpi: Kpi; renderLink?: RenderLink }): ReactNode {
  if (kpi.onPress) {
    return (
      <button
        type="button"
        className="ad-kpi ad-kpi--live"
        aria-pressed={kpi.pressed}
        onClick={kpi.onPress}
      >
        <Body kpi={kpi} />
      </button>
    );
  }
  if (kpi.href && renderLink && !kpi.href.startsWith('#')) {
    return <>{renderLink(kpi.href, <Body kpi={kpi} />, 'ad-kpi ad-kpi--live')}</>;
  }
  if (kpi.href) {
    return (
      <a className="ad-kpi ad-kpi--live" href={kpi.href}>
        <Body kpi={kpi} />
      </a>
    );
  }
  return (
    <div className="ad-kpi">
      <Body kpi={kpi} />
    </div>
  );
}

/** A row of tiles that wraps to two columns on a phone. */
export function KpiRow(props: {
  kpis: readonly Kpi[];
  label: string;
  renderLink?: RenderLink | undefined;
}): ReactNode {
  const { kpis, label, renderLink } = props;
  return (
    <section className="ad-kpis" aria-label={label}>
      {kpis.map((k) => (
        <KpiTile key={k.label} kpi={k} {...(renderLink ? { renderLink } : {})} />
      ))}
    </section>
  );
}
